const REGION_FLAGS = {
  DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', NL: '🇳🇱', PT: '🇵🇹',
  UK: '🇬🇧', ES: '🇪🇸', SE: '🇸🇪', PL: '🇵🇱',
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', BR: '🇧🇷', CO: '🇨🇴', AR: '🇦🇷',
  AU: '🇦🇺', JP: '🇯🇵', SG: '🇸🇬', AE: '🇦🇪', IN: '🇮🇳',
};

function fmtRate(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(rate >= 0.1 ? 0 : 1)}%`;
}

// Tiny inline sparkline of daily ban counts — no chart library
function Sparkline({ data, color = '#f87171', width = 96, height = 26 }) {
  const vals = data.map(d => d.bans);
  const max  = Math.max(1, ...vals);
  const n    = vals.length;
  const step = n > 1 ? width / (n - 1) : width;
  const pts  = vals.map((v, i) => `${(i * step).toFixed(1)},${(height - (v / max) * height).toFixed(1)}`).join(' ');
  const areaPts = `0,${height} ${pts} ${width},${height}`;

  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      <polygon points={areaPts} fill={color} opacity={0.12} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5}
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Kpi({ label, value, sub, accent, onClick, active, isMobile, children }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        flex: 1, minWidth: isMobile ? 'calc(50% - 5px)' : 120,
        textAlign: 'left',
        background: active ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${active ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.07)'}`,
        borderRadius: 12,
        padding: isMobile ? '11px 13px' : '13px 16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.14s',
      }}
    >
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6, marginTop: 5 }}>
        <div style={{ fontSize: isMobile ? 21 : 25, fontWeight: 700, color: accent || '#f1f5f9', lineHeight: 1, letterSpacing: '-0.5px' }}>
          {value}
        </div>
        {children}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: '#475569', marginTop: 4 }}>{sub}</div>}
    </button>
  );
}

export default function SummaryStrip({ provider, health, onJump, activeTab, isMobile }) {
  const acc     = provider?.accountCounts || { active: 0, suspended: 0, total: 0 };
  const orders  = provider?.orderCounts   || { active: 0, total: 0 };
  const invites = provider?.invitationCount ?? 0;
  const o       = health?.overall || {};

  const trend = (o.bansLast7Days ?? 0) - (o.bansPrev7Days ?? 0);
  const trendLabel = o.bansPrev7Days == null ? null
    : trend > 0 ? `▲ ${trend} vs prev week`
    : trend < 0 ? `▼ ${Math.abs(trend)} vs prev week`
    : '– flat vs prev week';
  const trendColor = trend > 0 ? '#f87171' : trend < 0 ? '#34d399' : '#64748b';

  // Domain league — worst ban rate first, with region flags underneath
  const domains = (health?.dimensions?.find(d => d.id === 'domain')?.groups || []);

  return (
    <div style={{ marginBottom: isMobile ? 16 : 20 }}>
      {/* KPI row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 10, marginBottom: 12 }}>
        <Kpi
          label="Active accounts"
          value={acc.active.toLocaleString()}
          sub={`of ${acc.total.toLocaleString()} created`}
          accent="#34d399"
          onClick={() => onJump('Accounts')}
          active={activeTab === 'Accounts'}
          isMobile={isMobile}
        />
        <Kpi
          label="Bans · 7 days"
          value={(o.bansLast7Days ?? 0).toLocaleString()}
          sub={trendLabel}
          accent="#f87171"
          onClick={() => onJump('Health')}
          active={activeTab === 'Health'}
          isMobile={isMobile}
        >
          {o.timeline && <Sparkline data={o.timeline} />}
        </Kpi>
        <Kpi
          label="Ban rate · 7d"
          value={fmtRate(o.recentBanRate)}
          sub={`${fmtRate(o.banRate)} all-time`}
          accent={trendColor}
          onClick={() => onJump('Health')}
          active={activeTab === 'Health'}
          isMobile={isMobile}
        />
        <Kpi
          label="Active invites"
          value={invites.toLocaleString()}
          sub="within 72h window"
          accent={invites > 0 ? '#fbbf24' : '#475569'}
          onClick={() => onJump('Invitations')}
          active={activeTab === 'Invitations'}
          isMobile={isMobile}
        />
        <Kpi
          label="Active orders"
          value={(orders.active ?? 0).toLocaleString()}
          sub={`${orders.total.toLocaleString()} all-time`}
          accent={orders.active > 0 ? '#60a5fa' : '#475569'}
          onClick={() => onJump('Orders')}
          active={activeTab === 'Orders'}
          isMobile={isMobile}
        />
      </div>

      {/* Domain league strip with region flags */}
      {domains.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2,
          scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
        }}>
          {domains.map(d => {
            const rate = d.banRate;
            const color = rate === null || rate >= 0.5 ? '#f87171' : rate >= 0.15 ? '#fbbf24' : '#34d399';
            const regions = (d.breakdown?.find(b => b.id === 'region')?.groups || [])
              .filter(r => r.key !== 'unknown').slice(0, 5);
            return (
              <button
                key={d.key}
                onClick={() => onJump('Health')}
                style={{
                  flexShrink: 0, textAlign: 'left', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 10, padding: '9px 13px', minWidth: 150,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap' }}>{d.key}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{fmtRate(rate)}</span>
                </div>
                <div style={{ fontSize: 10.5, color: '#475569', marginTop: 2 }}>
                  {d.banned.toLocaleString()} bans · {d.total.toLocaleString()} accts
                </div>
                {regions.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                    {regions.map(r => (
                      <span key={r.key} title={`${r.key}: ${fmtRate(r.banRate)} ban rate`} style={{
                        fontSize: 11, color: '#94a3b8',
                        background: 'rgba(255,255,255,0.05)', borderRadius: 5, padding: '0 5px',
                        whiteSpace: 'nowrap',
                      }}>
                        {REGION_FLAGS[r.key] || '🌐'} {r.key}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
